import { useState } from 'react';
import api from '../services/api';

// Premium Account Settings - Personal Command Center
function AccountSettings() {
    const [user] = useState((window as any).CURRENT_USER || {});
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        password: '',
        password_confirmation: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<any>(null);

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            const response = await api.put('/auth/password', { user: passwordData });
            if (response.success) {
                setMessage({ type: 'success', text: 'Security credentials synchronized successfully.' });
                setPasswordData({ current_password: '', password: '', password_confirmation: '' });
            } else {
                setMessage({ type: 'error', text: response.message || 'Synchronization failure during security registry update.' });
            }
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || 'An operational error occurred.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header section */}
            <div className="mb-12">
                <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
                    Account <span className="text-indigo-600">Commander</span>
                </h1>
                <p className="text-slate-500 mt-2 font-medium italic">Manage personal identity registry and security credentials</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                {/* Left: Identity Card */}
                <div className="lg:col-span-1">
                    <div className="sticky top-24">
                        <div className="bg-white rounded-lg border border-slate-100 shadow-2xl shadow-slate-200/50 p-8 overflow-hidden group">
                            <div className="relative z-10 text-center">
                                <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center text-4xl text-white mx-auto mb-6 shadow-xl shadow-indigo-200 group-hover:rotate-6 transition-transform duration-500">
                                    {user.name?.[0] || 'U'}
                                </div>
                                <h3 className="text-xl font-black text-slate-900 tracking-tight">{user.name || 'Anonymous Agent'}</h3>
                                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mt-1">Authenticated Member</p>

                                <div className="mt-10 space-y-4 text-left">
                                    <div>
                                        <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 ml-1">Identity UID</div>
                                        <div className="px-4 py-3 bg-slate-50 rounded-xl text-xs font-bold text-slate-600 font-mono italic">
                                            {user.email || 'unset@registry.net'}
                                        </div>
                                    </div>
                                    <div className="p-4 bg-indigo-50/50 border border-indigo-100/50 rounded-2xl">
                                        <p className="text-[10px] font-bold text-indigo-700 leading-relaxed italic">
                                            ⚠️ Contact organization administrator to authorize registry identifier (Email/Name) changes.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/30 blur-3xl -mr-16 -mt-16 rounded-full group-hover:scale-150 transition-transform duration-1000"></div>
                        </div>
                    </div>
                </div>

                {/* Right: Security & Config */}
                <div className="lg:col-span-2 space-y-10">
                    <div className="bg-white rounded-lg border border-slate-100 shadow-2xl shadow-slate-200/50 p-10 overflow-hidden relative group">
                        <h3 className="text-xl font-black text-slate-900 tracking-tight mb-8 flex items-center gap-3 relative z-10">
                            <span className="w-10 h-10 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center text-xl shadow-inner group-hover:rotate-12 transition-transform">🛡️</span>
                            Security Core Synchronization
                        </h3>

                        {message && (
                            <div className={`p-6 rounded-3xl mb-8 border-l-4 animate-in slide-in-from-left-4 duration-500 ${message.type === 'success' ? 'bg-emerald-50 border-emerald-500 text-emerald-800' : 'bg-rose-50 border-rose-500 text-rose-800'}`}>
                                <div className="text-[10px] font-black uppercase tracking-widest mb-1">{message.type === 'success' ? 'Synchronized' : 'Sync Error'}</div>
                                <div className="text-sm font-bold opacity-90">{message.text}</div>
                            </div>
                        )}

                        <form onSubmit={handlePasswordChange} className="space-y-8 relative z-10">
                            <div className="grid grid-cols-1 gap-8">
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 ml-1">Current authorization Key</label>
                                    <input
                                        type="password"
                                        required
                                        value={passwordData.current_password}
                                        onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                                        className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 text-sm font-black text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all hover:bg-slate-100"
                                        placeholder="••••••••••••"
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 ml-1">New access key (Entropy)</label>
                                        <input
                                            type="password"
                                            required
                                            value={passwordData.password}
                                            onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                                            className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 text-sm font-black text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all hover:bg-slate-100"
                                            placeholder="NEW SECURE PHRASE"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-3 ml-1">Verify new access key</label>
                                        <input
                                            type="password"
                                            required
                                            value={passwordData.password_confirmation}
                                            onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                                            className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 text-sm font-black text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all hover:bg-slate-100"
                                            placeholder="RE-ENTRY VERIFICATION"
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full md:w-auto px-12 py-5 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-[0.25em] shadow-2xl shadow-slate-200 hover:bg-indigo-600 hover:shadow-indigo-200 transition-all duration-500 disabled:opacity-50"
                            >
                                {loading ? 'Synchronizing Keyrings...' : 'Commit Security Update'}
                            </button>
                        </form>
                        <div className="absolute top-0 right-0 w-80 h-80 bg-rose-50/20 blur-3xl -mr-40 -mt-40 rounded-full group-hover:scale-125 transition-transform duration-1000"></div>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-lg p-10 text-white shadow-2xl shadow-indigo-200 relative overflow-hidden group">
                        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                            <div>
                                <h3 className="text-2xl font-black tracking-tight mb-2">Member Session Active</h3>
                                <p className="text-white/70 font-bold italic text-sm">Your account is currently synchronized with the global inventory network.</p>
                            </div>
                            <div className="flex -space-x-4">
                                <div className="w-12 h-12 bg-white/10 rounded-full border-2 border-white/20 backdrop-blur-md flex items-center justify-center text-sm font-black ring-4 ring-white/10">S</div>
                                <div className="w-12 h-12 bg-white/20 rounded-full border-2 border-white/20 backdrop-blur-md flex items-center justify-center text-sm font-black ring-4 ring-white/10">Y</div>
                                <div className="w-12 h-12 bg-white/30 rounded-full border-2 border-white/20 backdrop-blur-md flex items-center justify-center text-sm font-black ring-4 ring-white/10">N</div>
                                <div className="w-12 h-12 bg-indigo-500 rounded-full border-2 border-white/20 backdrop-blur-md flex items-center justify-center text-sm font-black ring-4 ring-white/10 animate-pulse">C</div>
                            </div>
                        </div>
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 blur-3xl -mr-32 -mt-32 rounded-full group-hover:scale-150 transition-transform duration-1000"></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AccountSettings;
